/**
 * Orden y comparación de PK a lo largo de una carretera.
 *
 * ─── Por qué NO vale `km * 1000 + meter` ───────────────────────────────────
 *
 * Porque **hay kilómetros largos de verdad**. Medido recorriendo la C-17 por el
 * terreno (vértices cada 100 m, cadena continua): el kilómetro 84 mide **1.890
 * metros**, o sea que la numeración va `84+000 … 84+1800` y solo entonces salta
 * a `85+000`. Es una vía realineada que no se renumeró, y `84+1800` es un dato
 * **bueno**.
 *
 * Con esa realidad, el total en metros deja de ordenar:
 *
 *   84+1800 → 85800        85+000 → 85000
 *
 * El total dice que `85+000` va ANTES, y en el asfalto va **después**. Y además
 * colisiona: `84+1800` y `85+800` dan el mismo número estando a 888 m.
 * Auditadas las 7 vías de beta: **117 colisiones**, la peor de **854 m**.
 *
 * ─── La regla ──────────────────────────────────────────────────────────────
 *
 * El PK es una etiqueta jerárquica, no un número: se compara **primero el
 * kilómetro y luego los metros**. Sustituye al total en TODO lo que sea
 * comparar, ordenar o saber si algo sube o baja.
 *
 * 🔴 Lo que esto NO resuelve: **medir**. La distancia entre dos PK no es la
 * resta de sus etiquetas cuando hay un kilómetro largo por medio
 * (`85+000 − 84+1800` daría −800 m cuando en el suelo son +90). Para eso hay
 * que medir sobre la malla. Ver `.claude/rules/pk-kilometro-largo.md`.
 *
 * Gemelo del backend en `rois-back/src/utils/pk-compare.helper.ts`, con un test
 * de paridad que usa los mismos números a los dos lados.
 */
export interface PkLike {
  km: number;
  meter: number;
}

/**
 * Negativo si `a` va antes que `b`, positivo si va después, 0 si son el mismo
 * PK. Sirve tal cual como comparador de `Array.prototype.sort`.
 */
export const comparePk = (a: PkLike, b: PkLike): number =>
  a.km !== b.km ? a.km - b.km : a.meter - b.meter;

/** ¿Son exactamente el mismo PK? */
export const pkEquals = (a: PkLike, b: PkLike): boolean =>
  comparePk(a, b) === 0;

/**
 * ¿`pk` cae entre `a` y `b`, extremos incluidos? El orden en que se pasen da
 * igual: hay carreteras dadas de alta al revés (la C-65 va de 9+800 a 2+340) y
 * recorridos que se patrullan bajando.
 */
export const pkIsBetween = (pk: PkLike, a: PkLike, b: PkLike): boolean => {
  const [min, max] = comparePk(a, b) <= 0 ? [a, b] : [b, a];
  return comparePk(pk, min) >= 0 && comparePk(pk, max) <= 0;
};

/**
 * Hacia dónde avanza ir de `from` a `to`: `1` sube de PK, `-1` baja, `0` no se
 * mueve.
 *
 * Sustituye a `Math.sign(total(to) - total(from))`, que con un kilómetro largo
 * por medio puede devolver el signo **contrario**.
 */
export const pkDirection = (from: PkLike, to: PkLike): -1 | 0 | 1 => {
  const c = comparePk(from, to);
  return c === 0 ? 0 : c < 0 ? 1 : -1;
};

/** Ordena una lista de PK a lo largo de la carretera, sin mutar la original. */
export const sortByPk = <T extends PkLike>(items: T[]): T[] =>
  [...items].sort(comparePk);

/**
 * Los dos extremos ordenados: primero el que va antes en la carretera.
 *
 * Hace falta porque hay carreteras dadas de alta al revés y tramos patrullados
 * bajando: quien recibe un par no puede dar por hecho cuál es el mínimo.
 */
export const pkMinMax = (a: PkLike, b: PkLike): [PkLike, PkLike] =>
  comparePk(a, b) <= 0 ? [a, b] : [b, a];

/**
 * ¿Se pisan estos dos rangos de PK? Extremos incluidos: dos tramos que se tocan
 * justo en un punto **sí** se consideran solapados, que es lo que la validación
 * de correcciones quiere saber.
 *
 * Sustituye al patrón `Math.min/Math.max` sobre el total en metros, que con un
 * kilómetro largo por medio puede construir un rango del revés.
 */
export const pkRangesOverlap = (
  a1: PkLike,
  b1: PkLike,
  a2: PkLike,
  b2: PkLike
): boolean => {
  const [min1, max1] = pkMinMax(a1, b1);
  const [min2, max2] = pkMinMax(a2, b2);
  return comparePk(min1, max2) <= 0 && comparePk(min2, max1) <= 0;
};

/** Atajo para los muchos sitios que llevan los PK en campos sueltos. */
export const asPk = (
  km?: number | null,
  meter?: number | null
): PkLike => ({ km: km ?? 0, meter: meter ?? 0 });
